import React from 'react';

import {
  buildAttachmentDisplayGroups,
  facilityPowerSplitGroupStageKey,
  getFacilityPowerAttachmentGroupLabel,
  getFacilityPowerAttachmentGroupNavOrder,
  getGroupedAttachmentGroupNavOrder,
  isFacilityPowerFamilyAttachmentStage,
  isFacilityPowerFamilySplitScopeMode,
  parseFacilityPowerSplitGroupStageKey,
  resolveAttachmentScopeOptions,
  resolveFacilityPowerStepForAttachmentSection,
  resolveStoredStepIdsForAttachments,
  shouldIncludeAttachmentSectionInScope,
  type AttachmentScopeOptions,
} from './facilityPowerAttachmentGrouping';
import { FileText, ImageIcon, VideoIcon, Volume2 } from '../components/icons';
import { getRequestFormStepCatalogEntry } from '../config/requestFormStepRegistry';
import {
  getSectionLabel,
  getStageFromSection,
  resolveSectionIdForLookup,
  stripStageNumber,
  STAGE_LABELS,
  STAGE_ORDER,
  STAGE_SHORT_LABELS,
  STAGES,
  type Stage,
} from '../constants/sectionIds';
import {
  debugAttachmentGrouping,
  debugAttachmentGroupingPath,
  summarizeAttachmentIdList,
  summarizeDisplayGroups,
  summarizeGroupedAttachments,
  summarizeSectionAttachmentCounts,
  summarizeVisibleAttachmentItems,
} from '../dev/attachmentGroupingDebug';
import { type SavedAttachment } from '../services/attachmentService';
import {
  isImageMimeType,
  isVideoMimeType,
  isAudioMimeType,
} from '../services/mediaViewerService';
import { type GroupedAttachments } from '../types/attachmentInterfaces';

export type { AttachmentScopeOptions };

export { resolveAttachmentScopeOptions, resolveStoredStepIdsForAttachments };

/**
 * Check if a MIME type is PDF
 */
export const isPDFType = (mimeType?: string): boolean => {
  return mimeType === 'application/pdf';
};

/**
 * Get appropriate icon component for attachment based on MIME type
 */
export const getAttachmentIcon = (
  mimeType?: string,
  size: number = 24,
): React.ReactElement => {
  if (isPDFType(mimeType)) {
    return React.createElement(FileText, { size, color: '#d32f2f' });
  }
  if (isImageMimeType(mimeType || '')) {
    return React.createElement(ImageIcon, { size, color: '#1976d2' });
  }
  if (isVideoMimeType(mimeType || '')) {
    return React.createElement(VideoIcon, { size, color: '#7b1fa2' });
  }
  if (isAudioMimeType(mimeType || '')) {
    return React.createElement(Volume2, { size, color: '#f57c00' });
  }
  // Default file icon
  return React.createElement(FileText, { size, color: '#616161' });
};

function siteInformationAttachmentStageLabel(): string {
  return (
    getRequestFormStepCatalogEntry('site-information')?.title ??
    'Site Information'
  );
}

function coolingReadinessAttachmentStageLabel(): string {
  return (
    getRequestFormStepCatalogEntry('cooling-readiness')?.title ?? 'Cooling'
  );
}

/** Nav-aligned stage header for grouped attachments + Summary appendix. */
export function resolveAttachmentGroupStageDisplayLabel(
  groupKey: string | null | undefined,
  displayLabel?: string,
): string {
  const safeGroupKey = resolveSafeGroupKey(groupKey);
  const fallback =
    displayLabel ?? STAGE_LABELS[safeGroupKey as Stage] ?? safeGroupKey;
  return resolveGroupedStageLabel(safeGroupKey, fallback);
}

function resolveGroupedStageLabel(
  groupKey: string | null | undefined,
  displayLabel: string,
): string {
  const safeGroupKey = resolveSafeGroupKey(groupKey);
  const splitStepId = parseFacilityPowerSplitGroupStageKey(safeGroupKey);
  if (splitStepId) {
    return displayLabel;
  }
  if (safeGroupKey === STAGES.SITE_DIMENSIONS) {
    return siteInformationAttachmentStageLabel();
  }
  if (safeGroupKey === STAGES.COOLING_READINESS) {
    return coolingReadinessAttachmentStageLabel();
  }
  const stage = safeGroupKey as (typeof STAGE_ORDER)[number];
  return STAGE_LABELS[stage] || displayLabel;
}

function groupedSubstageLabelPrefixes(
  stageLabel: string,
  groupKey?: string,
): string[] {
  const prefixes = new Set<string>();
  const strippedStage = stripStageNumber(stageLabel).trim();
  if (strippedStage) {
    prefixes.add(strippedStage);
  }
  if (groupKey && groupKey in STAGE_SHORT_LABELS) {
    prefixes.add(STAGE_SHORT_LABELS[groupKey as Stage]);
  }
  if (groupKey === STAGES.SITE_DIMENSIONS) {
    prefixes.add(siteInformationAttachmentStageLabel());
    prefixes.add('Site Dimensions');
  }
  if (groupKey === STAGES.COOLING_READINESS) {
    prefixes.add(coolingReadinessAttachmentStageLabel());
    prefixes.add('Cooling Readiness');
  }
  const familyStep = parseFacilityPowerSplitGroupStageKey(groupKey ?? '');
  if (familyStep) {
    prefixes.add(getFacilityPowerAttachmentGroupLabel(familyStep));
  }
  return [...prefixes];
}

/**
 * Substage header under a grouped stage — subsection only (no repeated step prefix).
 * e.g. stage "Liquid Cooling" + label "Liquid Cooling - Project Requirements" → "Project Requirements"
 * When SECTION_LABELS already matches the step card ("Liquid Cooling"), the substage header is hidden.
 */
export function formatGroupedSubstageLabel(
  sectionId: string,
  stageLabel: string,
  groupKey?: string,
): string {
  const fullLabel = getSectionLabel(sectionId);
  for (const prefix of groupedSubstageLabelPrefixes(stageLabel, groupKey)) {
    const dashPrefix = `${prefix} - `;
    if (fullLabel.startsWith(dashPrefix)) {
      return fullLabel.slice(dashPrefix.length);
    }
  }
  return fullLabel;
}

function resolveGroupedSubstageSectionLabel(
  sectionId: string,
  stageLabel: string,
  groupKey: string,
): string {
  return formatGroupedSubstageLabel(sectionId, stageLabel, groupKey);
}

/** Always return a string stage key for grouped-view buckets (#8070). */
function resolveAttachmentGroupStageKey(sectionId: string): string {
  return (
    getStageFromSection(sectionId) ??
    STAGES.SUPPORTING_DOCUMENTS ??
    'supporting-documents'
  );
}

function resolveGroupedBucketForSection(
  sectionId: string,
  options: AttachmentScopeOptions,
): { groupKey: string; stageLabel: string } {
  const lookupSectionId = resolveSectionIdForLookup(sectionId);
  const stage =
    getStageFromSection(sectionId) ??
    getStageFromSection(lookupSectionId) ??
    STAGES.SUPPORTING_DOCUMENTS;

  let groupKey = stage;
  let stageLabel = STAGE_LABELS[stage] ?? stage;

  if (isFacilityPowerFamilyAttachmentStage(stage)) {
    const familyStep =
      resolveFacilityPowerStepForAttachmentSection(lookupSectionId, options) ??
      resolveFacilityPowerStepForAttachmentSection(sectionId, options) ??
      'facility-power';
    groupKey = facilityPowerSplitGroupStageKey(familyStep);
    stageLabel = getFacilityPowerAttachmentGroupLabel(familyStep);
  } else if (stage === STAGES.SITE_DIMENSIONS) {
    stageLabel = siteInformationAttachmentStageLabel();
  } else if (stage === STAGES.COOLING_READINESS) {
    stageLabel = coolingReadinessAttachmentStageLabel();
  }

  const safeGroupKey = resolveSafeGroupKey(groupKey);
  return {
    groupKey: safeGroupKey,
    stageLabel: resolveGroupedStageLabel(safeGroupKey, stageLabel),
  };
}

function resolveSafeGroupKey(groupKey: string | null | undefined): string {
  return groupKey ?? STAGES.SUPPORTING_DOCUMENTS ?? 'supporting-documents';
}

function normalizeAttachmentId(id: unknown): number | null {
  if (id == null || id === '') {
    return null;
  }
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  const parsed = Number.parseInt(String(id), 10);
  return Number.isNaN(parsed) ? null : parsed;
}

function stripAttachmentSectionLabel<T extends SavedAttachment>(
  attachment: T & { _sectionLabel?: string },
): T {
  const { _sectionLabel: _ignored, ...rest } = attachment;
  return rest as T;
}

function stripFlatAttachments(
  flatAttachments: SavedAttachment[],
): SavedAttachment[] {
  return flatAttachments
    .filter((attachment): attachment is SavedAttachment => attachment != null)
    .map(stripAttachmentSectionLabel);
}

function resolveSubstageSectionId(
  sectionId: string | null | undefined,
): string {
  const trimmed = sectionId?.trim();
  return trimmed ? trimmed : 'unknown';
}

function normalizeGroupedAttachments(
  groups: GroupedAttachments[] | null | undefined,
  context?: string,
): GroupedAttachments[] {
  const inputSummary = summarizeGroupedAttachments(groups);
  const result = (groups ?? [])
    .map(group => {
      const safeStage = resolveSafeGroupKey(group?.stage);
      return {
        stage: safeStage,
        stageLabel: resolveAttachmentGroupStageDisplayLabel(
          safeStage,
          group?.stageLabel,
        ),
        substages: (group?.substages ?? [])
          .map(substage => ({
            sectionId: resolveSubstageSectionId(substage?.sectionId),
            sectionLabel: substage?.sectionLabel ?? '',
            attachments: coerceSectionAttachmentList(substage?.attachments),
          }))
          .filter(substage => (substage.attachments ?? []).length > 0),
      };
    })
    .filter(group => group.substages.length > 0);
  const outputSummary = summarizeGroupedAttachments(result);
  if (
    context &&
    (outputSummary.attachmentCount < inputSummary.attachmentCount ||
      outputSummary.groupCount < inputSummary.groupCount)
  ) {
    debugAttachmentGrouping(`normalizeGroupedAttachments.${context}`, {
      input: inputSummary,
      output: outputSummary,
      droppedAttachments:
        inputSummary.attachmentCount - outputSummary.attachmentCount,
      droppedGroups: inputSummary.groupCount - outputSummary.groupCount,
    });
  }
  return result;
}

export function coerceSectionAttachmentList(
  rawAttachments: SavedAttachment[] | SavedAttachment | null | undefined,
): SavedAttachment[] {
  const list = Array.isArray(rawAttachments)
    ? rawAttachments
    : rawAttachments != null
    ? [rawAttachments]
    : [];
  return list.filter(
    (attachment): attachment is SavedAttachment => attachment != null,
  );
}

function buildGroupedSubstages(
  groupKey: string | null | undefined,
  stageLabel: string | null | undefined,
  items:
    | Array<{ sectionId: string; attachment: SavedAttachment }>
    | null
    | undefined,
): GroupedAttachments['substages'] {
  const safeGroupKey = resolveSafeGroupKey(groupKey);
  const safeStageLabel =
    stageLabel ?? STAGE_LABELS[safeGroupKey as Stage] ?? safeGroupKey;
  const substageMap = new Map<string, SavedAttachment[]>();
  for (const entry of items ?? []) {
    if (entry == null) {
      continue;
    }
    const { sectionId, attachment } = entry;
    if (attachment == null) {
      continue;
    }
    const safeSectionId = resolveSubstageSectionId(sectionId);
    const bucket = substageMap.get(safeSectionId) ?? [];
    bucket.push(attachment);
    substageMap.set(safeSectionId, bucket);
  }

  const substages = [...substageMap.entries()].map(
    ([sectionId, sectionAttachments]) => ({
      sectionId,
      sectionLabel: resolveGroupedSubstageSectionLabel(
        sectionId,
        safeStageLabel,
        safeGroupKey,
      ),
      attachments: sectionAttachments,
    }),
  );

  if ((items ?? []).length > 0 && substages.length === 0) {
    debugAttachmentGrouping('buildGroupedSubstages.empty', {
      groupKey: safeGroupKey,
      stageLabel: safeStageLabel,
      itemCount: items?.length ?? 0,
      items: summarizeVisibleAttachmentItems(items ?? []),
    });
  }

  return substages;
}

function buildAttachmentIdToSectionMap(
  attachmentsBySection: Record<string, SavedAttachment[]>,
): Map<number, string> {
  const idToSection = new Map<number, string>();
  for (const [sectionId, rawAttachments] of Object.entries(
    attachmentsBySection,
  )) {
    for (const attachment of coerceSectionAttachmentList(rawAttachments)) {
      const attachmentId = normalizeAttachmentId(attachment.id);
      if (attachmentId != null) {
        idToSection.set(attachmentId, sectionId);
      }
    }
  }
  return idToSection;
}

function resolveAttachmentSectionForFlatRow(
  attachment: SavedAttachment,
  idToSection: Map<number, string>,
  attachmentsBySection: Record<string, SavedAttachment[]>,
): string {
  const attachmentId = normalizeAttachmentId(attachment.id);
  if (attachmentId != null) {
    const fromMap = idToSection.get(attachmentId);
    if (fromMap) {
      return fromMap;
    }
    for (const [sectionId, rawAttachments] of Object.entries(
      attachmentsBySection,
    )) {
      if (
        coerceSectionAttachmentList(rawAttachments).some(
          candidate => normalizeAttachmentId(candidate.id) === attachmentId,
        )
      ) {
        return sectionId;
      }
    }
  }

  const requestSection = attachment.requestSection?.trim();
  if (requestSection) {
    return requestSection;
  }

  return 'unknown';
}

function flatContainsAttachment(
  flatAttachments: SavedAttachment[],
  flatIds: Set<number>,
  attachment: SavedAttachment,
): boolean {
  const attachmentId = normalizeAttachmentId(attachment.id);
  if (attachmentId != null && flatIds.has(attachmentId)) {
    return true;
  }
  return flatAttachments.some(candidate => {
    if (candidate === attachment) {
      return true;
    }
    const candidateId = normalizeAttachmentId(candidate.id);
    return (
      candidateId != null &&
      attachmentId != null &&
      candidateId === attachmentId
    );
  });
}

/** Collect the same attachment rows flat Supporting Documents already shows. */
function collectVisibleFlatAttachmentItems(
  flatAttachments: SavedAttachment[],
  attachmentsBySection: Record<string, SavedAttachment[]>,
  options: AttachmentScopeOptions,
  idToSection: Map<number, string>,
): Array<{ sectionId: string; attachment: SavedAttachment }> {
  const items: Array<{ sectionId: string; attachment: SavedAttachment }> = [];
  const flatIds = new Set<number>();
  for (const attachment of flatAttachments) {
    const attachmentId = normalizeAttachmentId(attachment.id);
    if (attachmentId != null) {
      flatIds.add(attachmentId);
    }
  }

  for (const [sectionId, rawAttachments] of Object.entries(
    attachmentsBySection,
  )) {
    if (!shouldIncludeAttachmentSectionInScope(sectionId, options)) {
      continue;
    }
    for (const attachment of coerceSectionAttachmentList(rawAttachments)) {
      if (
        flatAttachments.length > 0 &&
        !flatContainsAttachment(flatAttachments, flatIds, attachment)
      ) {
        continue;
      }
      items.push({
        sectionId,
        attachment: {
          ...attachment,
          requestSection: attachment.requestSection ?? sectionId,
        },
      });
    }
  }

  if (items.length === 0) {
    for (const attachment of flatAttachments) {
      if (attachment == null) {
        continue;
      }
      const sectionId = resolveAttachmentSectionForFlatRow(
        attachment,
        idToSection,
        attachmentsBySection,
      );
      items.push({
        sectionId,
        attachment: {
          ...attachment,
          requestSection: attachment.requestSection ?? sectionId,
        },
      });
    }
  }

  debugAttachmentGrouping('collectVisibleFlatAttachmentItems', {
    flatCount: flatAttachments.length,
    flatIds: summarizeAttachmentIdList(flatAttachments),
    sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    visibleItemCount: items.length,
    visibleItems: summarizeVisibleAttachmentItems(items),
  });

  return items;
}

function buildStageGroupsFromVisibleItems(
  items: Array<{ sectionId: string; attachment: SavedAttachment }>,
  options: AttachmentScopeOptions,
): GroupedAttachments[] {
  if (items.length === 0) {
    debugAttachmentGrouping('buildStageGroupsFromVisibleItems.skip', {
      reason: 'no visible items',
    });
    return [];
  }

  const attachmentsBySection: Record<string, SavedAttachment[]> = {};
  for (const { sectionId, attachment } of items) {
    const bucket = attachmentsBySection[sectionId] ?? [];
    bucket.push(attachment);
    attachmentsBySection[sectionId] = bucket;
  }

  const displayGroups = buildAttachmentDisplayGroups(
    attachmentsBySection,
    options,
  );
  const displaySummary = summarizeDisplayGroups(displayGroups);

  if (displayGroups.length > 0) {
    const groups = displayGroups.map(group => {
      const safeGroupKey = resolveSafeGroupKey(group.groupKey);
      const stageLabel = resolveGroupedStageLabel(safeGroupKey, group.label);
      return {
        stage: safeGroupKey,
        stageLabel,
        substages: buildGroupedSubstages(
          safeGroupKey,
          stageLabel,
          group.items ?? [],
        ),
      };
    });
    debugAttachmentGrouping('buildStageGroupsFromVisibleItems.displayGroups', {
      displaySummary,
      grouped: summarizeGroupedAttachments(groups),
    });
    return groups;
  }

  debugAttachmentGrouping('buildStageGroupsFromVisibleItems.fallbackStageMap', {
    displaySummary,
    reason: 'buildAttachmentDisplayGroups returned no groups',
  });

  const stageItems = new Map<
    string,
    Array<{ sectionId: string; attachment: SavedAttachment }>
  >();
  for (const item of items) {
    const stage = resolveAttachmentGroupStageKey(item.sectionId);
    const bucket = stageItems.get(stage) ?? [];
    bucket.push(item);
    stageItems.set(stage, bucket);
  }

  const fallbackGroups = [...stageItems.entries()]
    .sort(([stageA], [stageB]) => {
      const orderA = STAGE_ORDER.indexOf(stageA as Stage);
      const orderB = STAGE_ORDER.indexOf(stageB as Stage);
      return (
        (orderA >= 0 ? orderA : STAGE_ORDER.length) -
        (orderB >= 0 ? orderB : STAGE_ORDER.length)
      );
    })
    .map(([stage, stageItemsForStage]) => {
      const safeStage = resolveSafeGroupKey(stage);
      const stageLabel = STAGE_LABELS[safeStage as Stage] ?? safeStage;
      return {
        stage: safeStage,
        stageLabel,
        substages: buildGroupedSubstages(
          safeStage,
          stageLabel,
          stageItemsForStage,
        ),
      };
    });
  debugAttachmentGrouping('buildStageGroupsFromVisibleItems.fallbackResult', {
    grouped: summarizeGroupedAttachments(fallbackGroups),
  });
  return fallbackGroups;
}

function buildInScopeSectionMap(
  attachmentsBySection: Record<string, SavedAttachment[]>,
  options: AttachmentScopeOptions,
): Record<string, SavedAttachment[]> {
  const scoped: Record<string, SavedAttachment[]> = {};
  for (const [sectionId, rawAttachments] of Object.entries(
    attachmentsBySection,
  )) {
    if (!shouldIncludeAttachmentSectionInScope(sectionId, options)) {
      continue;
    }
    const attachments = coerceSectionAttachmentList(rawAttachments);
    if (attachments.length === 0) {
      continue;
    }
    scoped[sectionId] = attachments.map(attachment => ({
      ...attachment,
      requestSection: attachment.requestSection ?? sectionId,
    }));
  }
  return scoped;
}

/** Last-resort grouped rows when display-group builder returns nothing for flat rows (#8070). */
function buildSyntheticStageGroupsFromFlat(
  flatAttachments: SavedAttachment[],
  idToSection: Map<number, string>,
): GroupedAttachments[] {
  const stageItems = new Map<
    string,
    Array<{ sectionId: string; attachment: SavedAttachment }>
  >();

  for (const attachment of flatAttachments) {
    if (attachment == null) {
      continue;
    }
    const sectionId =
      (normalizeAttachmentId(attachment.id) != null
        ? idToSection.get(normalizeAttachmentId(attachment.id)!)
        : undefined) ??
      attachment.requestSection ??
      'unknown';
    const stage = resolveAttachmentGroupStageKey(sectionId);
    const items = stageItems.get(stage) ?? [];
    items.push({
      sectionId,
      attachment: {
        ...attachment,
        requestSection: attachment.requestSection ?? sectionId,
      },
    });
    stageItems.set(stage, items);
  }

  const groups = [...stageItems.entries()]
    .sort(([stageA], [stageB]) => {
      const orderA = STAGE_ORDER.indexOf(stageA as Stage);
      const orderB = STAGE_ORDER.indexOf(stageB as Stage);
      return (
        (orderA >= 0 ? orderA : STAGE_ORDER.length) -
        (orderB >= 0 ? orderB : STAGE_ORDER.length)
      );
    })
    .map(([stage, items]) => {
      const safeStage = resolveSafeGroupKey(stage);
      const stageLabel = STAGE_LABELS[safeStage as Stage] ?? safeStage;
      const safeItems = Array.isArray(items) ? items : [];
      return {
        stage: safeStage,
        stageLabel,
        substages: buildGroupedSubstages(safeStage, stageLabel, safeItems),
      };
    })
    .filter(group => group.substages.length > 0);

  debugAttachmentGrouping('buildSyntheticStageGroupsFromFlat', {
    flatCount: flatAttachments.length,
    flatIds: summarizeAttachmentIdList(flatAttachments),
    grouped: summarizeGroupedAttachments(groups),
  });
  return groups;
}

function readGroupedStageBucket(
  raw: unknown,
  fallbackLabel: string,
): { stageLabel: string; substages: GroupedAttachments['substages'] } {
  if (raw == null || typeof raw !== 'object') {
    return { stageLabel: fallbackLabel, substages: [] };
  }
  if (Array.isArray(raw)) {
    return {
      stageLabel: fallbackLabel,
      substages: raw as GroupedAttachments['substages'],
    };
  }
  const record = raw as {
    stageLabel?: string;
    substages?: GroupedAttachments['substages'];
  };
  return {
    stageLabel: record.stageLabel ?? fallbackLabel,
    substages: Array.isArray(record.substages) ? record.substages : [],
  };
}

function appendAttachmentToGroupedStageMap(
  stageMap: Map<
    string,
    { stageLabel: string; substages: GroupedAttachments['substages'] }
  >,
  sectionId: string,
  attachment: SavedAttachment,
  options: AttachmentScopeOptions,
): void {
  if (attachment == null) {
    return;
  }
  const { groupKey, stageLabel } = resolveGroupedBucketForSection(
    sectionId,
    options,
  );
  const bucket = readGroupedStageBucket(stageMap.get(groupKey), stageLabel);
  const substages = bucket.substages;
  const safeSectionId = resolveSubstageSectionId(sectionId);
  let substage = substages.find(entry => entry.sectionId === safeSectionId);
  if (!substage) {
    substage = {
      sectionId: safeSectionId,
      sectionLabel: '',
      attachments: [],
    };
    substages.push(substage);
  }
  if (!Array.isArray(substage.attachments)) {
    substage.attachments = [];
  }
  substage.attachments.push({
    ...attachment,
    requestSection: attachment.requestSection ?? safeSectionId,
  });
  stageMap.set(groupKey, {
    stageLabel: bucket.stageLabel ?? stageLabel,
    substages,
  });
}

function groupedStageMapToAttachments(
  stageMap: Map<
    string,
    { stageLabel: string; substages: GroupedAttachments['substages'] }
  >,
): GroupedAttachments[] {
  return [...stageMap.entries()]
    .sort(([stageA], [stageB]) => compareGroupedStageKeys(stageA, stageB))
    .flatMap(([stage, bucket]) => {
      if (bucket == null) {
        debugAttachmentGrouping('groupedStageMapToAttachments.skipped', {
          stage,
          reason: 'undefined bucket value',
        });
        return [];
      }
      const normalized = readGroupedStageBucket(
        bucket,
        STAGE_LABELS[stage as Stage] ?? stage,
      );
      const substages = normalized.substages
        .filter(substage => (substage?.attachments ?? []).length > 0)
        .map(substage => ({
          ...substage,
          sectionLabel: resolveGroupedSubstageSectionLabel(
            substage.sectionId,
            normalized.stageLabel,
            stage,
          ),
        }));
      if (substages.length === 0) {
        return [];
      }
      return [
        {
          stage,
          stageLabel: normalized.stageLabel,
          substages,
        },
      ];
    });
}

/** Grouped rows from the same visible flat items Supporting Documents already shows (#8070). */
function buildGroupedFromVisibleItemsDirect(
  visibleItems: Array<{ sectionId: string; attachment: SavedAttachment }>,
  options: AttachmentScopeOptions,
): GroupedAttachments[] {
  const stageMap = new Map<
    string,
    { stageLabel: string; substages: GroupedAttachments['substages'] }
  >();
  for (const { sectionId, attachment } of visibleItems) {
    appendAttachmentToGroupedStageMap(stageMap, sectionId, attachment, options);
  }
  const groups = groupedStageMapToAttachments(stageMap);
  debugAttachmentGrouping('buildGroupedFromVisibleItemsDirect', {
    visibleItemCount: visibleItems.length,
    visibleItems: summarizeVisibleAttachmentItems(visibleItems),
    grouped: summarizeGroupedAttachments(groups),
  });
  return groups;
}

/** Last-resort grouped rows built directly from flat list rows (skips normalize). */
function buildDirectFlatGroupedView(
  flatAttachments: SavedAttachment[],
  idToSection: Map<number, string>,
  attachmentsBySection: Record<string, SavedAttachment[]>,
  options: AttachmentScopeOptions,
): GroupedAttachments[] {
  const stageMap = new Map<
    string,
    { stageLabel: string; substages: GroupedAttachments['substages'] }
  >();

  for (const attachment of flatAttachments) {
    if (attachment == null) {
      continue;
    }
    const sectionId = resolveAttachmentSectionForFlatRow(
      attachment,
      idToSection,
      attachmentsBySection,
    );
    appendAttachmentToGroupedStageMap(stageMap, sectionId, attachment, options);
  }

  const groups = groupedStageMapToAttachments(stageMap);

  debugAttachmentGrouping('buildDirectFlatGroupedView', {
    flatCount: flatAttachments.length,
    flatIds: summarizeAttachmentIdList(flatAttachments),
    grouped: summarizeGroupedAttachments(groups),
  });
  return groups;
}

function warnGroupedEmptyDespiteFlatRows(
  flatCount: number,
  visibleItemCount: number,
  attachmentsBySection: Record<string, SavedAttachment[]>,
  options: AttachmentScopeOptions,
): void {
  debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.empty', {
    flatCount,
    visibleItemCount,
    sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    enabledStepIds: options.enabledStepIds,
    storedStepIds: options.storedStepIds ?? null,
  });
}

function compareGroupedStageKeys(stageA: string, stageB: string): number {
  return (
    getGroupedAttachmentGroupNavOrder(stageA) -
    getGroupedAttachmentGroupNavOrder(stageB)
  );
}

/**
 * Primary grouped-view builder (#8070): walk attachmentsBySection with the same
 * scope filter as flat Supporting Documents.
 */
export function buildGroupedAttachmentsFromSectionMap(
  attachmentsBySection:
    | Record<string, SavedAttachment[] | SavedAttachment | null | undefined>
    | null
    | undefined,
  options: AttachmentScopeOptions,
): GroupedAttachments[] {
  const groups: GroupedAttachments[] = [];
  const groupIndexByKey = new Map<string, number>();

  for (const [sectionId, rawAttachments] of Object.entries(
    attachmentsBySection ?? {},
  )) {
    if (!shouldIncludeAttachmentSectionInScope(sectionId, options)) {
      continue;
    }
    for (const attachment of coerceSectionAttachmentList(rawAttachments)) {
      if (attachment == null) {
        continue;
      }

      const { groupKey, stageLabel } = resolveGroupedBucketForSection(
        sectionId,
        options,
      );
      let groupIndex = groupIndexByKey.get(groupKey);
      if (groupIndex === undefined) {
        groups.push({
          stage: groupKey,
          stageLabel,
          substages: [],
        });
        groupIndex = groups.length - 1;
        groupIndexByKey.set(groupKey, groupIndex);
      }

      const group = groups[groupIndex];
      if (group == null) {
        continue;
      }

      const safeSectionId = resolveSubstageSectionId(sectionId);
      let substage = group.substages.find(
        entry => entry.sectionId === safeSectionId,
      );
      if (!substage) {
        substage = {
          sectionId: safeSectionId,
          sectionLabel: '',
          attachments: [],
        };
        group.substages.push(substage);
      }
      if (!Array.isArray(substage.attachments)) {
        substage.attachments = [];
      }
      substage.attachments.push({
        ...attachment,
        requestSection: attachment.requestSection ?? safeSectionId,
      });
    }
  }

  const result = groups
    .map(group => ({
      stage: group.stage,
      stageLabel: group.stageLabel,
      substages: (group.substages ?? [])
        .filter(substage => (substage.attachments ?? []).length > 0)
        .map(substage => ({
          ...substage,
          sectionLabel: resolveGroupedSubstageSectionLabel(
            substage.sectionId,
            group.stageLabel,
            group.stage,
          ),
        })),
    }))
    .filter(group => group.substages.length > 0)
    .sort((a, b) => compareGroupedStageKeys(a.stage, b.stage));

  debugAttachmentGrouping('buildGroupedAttachmentsFromSectionMap', {
    sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    enabledStepIds: options.enabledStepIds,
    storedStepIds: options.storedStepIds ?? null,
    grouped: summarizeGroupedAttachments(result),
  });

  return result;
}

/**
 * Build grouped view from the same flat rows Supporting Documents already shows.
 * Prefers section-keyed grouping; falls back to id→section map, then stage synthesis.
 */
export function buildGroupedAttachmentsMatchingFlatList(
  flatAttachments: SavedAttachment[],
  attachmentsBySection: Record<string, SavedAttachment[]>,
  options: AttachmentScopeOptions,
): GroupedAttachments[] {
  const strippedFlat = stripFlatAttachments(flatAttachments);
  debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.start', {
    flatInputCount: flatAttachments.length,
    flatStrippedCount: strippedFlat.length,
    flatIds: summarizeAttachmentIdList(strippedFlat),
    sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    enabledStepIds: options.enabledStepIds,
    storedStepIds: options.storedStepIds ?? null,
  });

  if (strippedFlat.length === 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      reason: 'no flat rows after strip',
      result: summarizeGroupedAttachments([]),
    });
    return [];
  }

  const fromSectionMap = buildGroupedAttachmentsFromSectionMap(
    attachmentsBySection,
    options,
  );
  debugAttachmentGroupingPath(
    'fromSectionMap',
    {
      flatStrippedCount: strippedFlat.length,
      sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    },
    summarizeGroupedAttachments(fromSectionMap),
    fromSectionMap.length > 0,
  );
  if (fromSectionMap.length > 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      chosenPath: 'fromSectionMap',
      result: summarizeGroupedAttachments(fromSectionMap),
    });
    return fromSectionMap;
  }

  const idToSection = buildAttachmentIdToSectionMap(attachmentsBySection);
  const visibleItems = collectVisibleFlatAttachmentItems(
    strippedFlat,
    attachmentsBySection,
    options,
    idToSection,
  );

  if (visibleItems.length > 0) {
    const fromVisibleDirect = buildGroupedFromVisibleItemsDirect(
      visibleItems,
      options,
    );
    debugAttachmentGroupingPath(
      'fromVisibleDirect',
      { visibleItemCount: visibleItems.length },
      summarizeGroupedAttachments(fromVisibleDirect),
      fromVisibleDirect.length > 0,
    );
    if (fromVisibleDirect.length > 0) {
      debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
        chosenPath: 'fromVisibleDirect',
        result: summarizeGroupedAttachments(fromVisibleDirect),
      });
      return fromVisibleDirect;
    }
  }

  const rawVisibleGroups = buildStageGroupsFromVisibleItems(
    visibleItems,
    options,
  );
  const fromVisibleFlat = normalizeGroupedAttachments(
    rawVisibleGroups,
    'fromVisibleFlat',
  );
  debugAttachmentGroupingPath(
    'fromVisibleFlat',
    {
      visibleItemCount: visibleItems.length,
      raw: summarizeGroupedAttachments(rawVisibleGroups),
    },
    summarizeGroupedAttachments(fromVisibleFlat),
    fromVisibleFlat.length > 0,
  );
  if (fromVisibleFlat.length > 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      chosenPath: 'fromVisibleFlat',
      result: summarizeGroupedAttachments(fromVisibleFlat),
    });
    return fromVisibleFlat;
  }

  if (visibleItems.length > 0) {
    const directFromVisible = buildGroupedFromVisibleItemsDirect(
      visibleItems,
      options,
    );
    debugAttachmentGroupingPath(
      'directFromVisible',
      { visibleItemCount: visibleItems.length },
      summarizeGroupedAttachments(directFromVisible),
      directFromVisible.length > 0,
    );
    if (directFromVisible.length > 0) {
      debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
        chosenPath: 'directFromVisible',
        result: summarizeGroupedAttachments(directFromVisible),
      });
      return directFromVisible;
    }
  }

  const inScopeSectionMap = buildInScopeSectionMap(
    attachmentsBySection,
    options,
  );
  debugAttachmentGrouping('buildInScopeSectionMap', {
    sectionCounts: summarizeSectionAttachmentCounts(inScopeSectionMap),
  });
  const rawFromSectionKeys = buildGroupedAttachmentsFromSections(
    inScopeSectionMap,
    options,
  );
  const fromSectionKeys = normalizeGroupedAttachments(
    rawFromSectionKeys,
    'fromSectionKeys',
  );
  debugAttachmentGroupingPath(
    'fromSectionKeys',
    { raw: summarizeGroupedAttachments(rawFromSectionKeys) },
    summarizeGroupedAttachments(fromSectionKeys),
    fromSectionKeys.length > 0,
  );
  if (fromSectionKeys.length > 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      chosenPath: 'fromSectionKeys',
      result: summarizeGroupedAttachments(fromSectionKeys),
    });
    return fromSectionKeys;
  }

  const derivedSectionMap: Record<string, SavedAttachment[]> = {};
  for (const attachment of strippedFlat) {
    const sectionId = resolveAttachmentSectionForFlatRow(
      attachment,
      idToSection,
      attachmentsBySection,
    );
    if (!shouldIncludeAttachmentSectionInScope(sectionId, options)) {
      debugAttachmentGrouping('derivedSectionMap.excluded', {
        sectionId,
        attachmentId: normalizeAttachmentId(attachment.id),
        enabledStepIds: options.enabledStepIds,
      });
      continue;
    }
    const bucket = derivedSectionMap[sectionId] ?? [];
    bucket.push({
      ...attachment,
      requestSection: attachment.requestSection ?? sectionId,
    });
    derivedSectionMap[sectionId] = bucket;
  }

  const rawFromDerivedKeys = buildGroupedAttachmentsFromSections(
    derivedSectionMap,
    options,
  );
  const fromDerivedKeys = normalizeGroupedAttachments(
    rawFromDerivedKeys,
    'fromDerivedKeys',
  );
  debugAttachmentGroupingPath(
    'fromDerivedKeys',
    {
      derivedSectionCounts: summarizeSectionAttachmentCounts(derivedSectionMap),
      raw: summarizeGroupedAttachments(rawFromDerivedKeys),
    },
    summarizeGroupedAttachments(fromDerivedKeys),
    fromDerivedKeys.length > 0,
  );
  if (fromDerivedKeys.length > 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      chosenPath: 'fromDerivedKeys',
      result: summarizeGroupedAttachments(fromDerivedKeys),
    });
    return fromDerivedKeys;
  }

  debugAttachmentGrouping(
    'buildGroupedAttachmentsMatchingFlatList.syntheticFallback',
    {
      flatCount: strippedFlat.length,
      visibleItemCount: visibleItems.length,
      sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
      derivedSectionCounts: summarizeSectionAttachmentCounts(derivedSectionMap),
      enabledStepIds: options.enabledStepIds,
    },
  );

  const rawSynthetic = buildSyntheticStageGroupsFromFlat(
    strippedFlat,
    idToSection,
  );
  const synthetic = normalizeGroupedAttachments(rawSynthetic, 'synthetic');
  debugAttachmentGroupingPath(
    'synthetic',
    { raw: summarizeGroupedAttachments(rawSynthetic) },
    summarizeGroupedAttachments(synthetic),
    synthetic.length > 0,
  );
  if (synthetic.length > 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      chosenPath: 'synthetic',
      result: summarizeGroupedAttachments(synthetic),
    });
    return synthetic;
  }

  const direct = buildDirectFlatGroupedView(
    strippedFlat,
    idToSection,
    attachmentsBySection,
    options,
  );
  debugAttachmentGroupingPath(
    'directFlat',
    {},
    summarizeGroupedAttachments(direct),
    direct.length > 0,
  );
  if (direct.length > 0) {
    debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
      chosenPath: 'directFlat',
      result: summarizeGroupedAttachments(direct),
    });
    return direct;
  }

  warnGroupedEmptyDespiteFlatRows(
    strippedFlat.length,
    visibleItems.length,
    attachmentsBySection,
    options,
  );
  debugAttachmentGrouping('buildGroupedAttachmentsMatchingFlatList.exit', {
    chosenPath: 'none',
    result: summarizeGroupedAttachments([]),
  });
  return [];
}

/**
 * Transform flat attachments array into grouped structure by stage.
 * When #1330 split scopes are active, facility-power family attachments render
 * as separate nav-aligned stage groups.
 */
export const groupAttachmentsByStage = (
  attachments: SavedAttachment[],
  options?: AttachmentScopeOptions,
  attachmentsBySection?: Record<string, SavedAttachment[]>,
): GroupedAttachments[] => {
  if (attachmentsBySection) {
    return buildGroupedAttachmentsMatchingFlatList(
      attachments,
      attachmentsBySection,
      options ?? resolveAttachmentScopeOptions({ storedStepIds: null }),
    );
  }

  const attachmentsBySectionFromFlat = attachments.reduce<
    Record<string, SavedAttachment[]>
  >((acc, attachment) => {
    const sectionId = attachment.requestSection || '';
    if (!acc[sectionId]) {
      acc[sectionId] = [];
    }
    acc[sectionId].push(attachment);
    return acc;
  }, {});

  return buildGroupedAttachmentsFromSections(
    attachmentsBySectionFromFlat,
    options ?? resolveAttachmentScopeOptions({ storedStepIds: null }),
  );
};

/** Group attachments using the same section-keyed map as flat Supporting Documents. */
export function buildGroupedAttachmentsFromSections(
  attachmentsBySection: Record<string, SavedAttachment[]>,
  options: AttachmentScopeOptions,
): GroupedAttachments[] {
  debugAttachmentGrouping('buildGroupedAttachmentsFromSections.start', {
    sectionCounts: summarizeSectionAttachmentCounts(attachmentsBySection),
    enabledStepIds: options.enabledStepIds,
    storedStepIds: options.storedStepIds ?? null,
  });

  const displayGroups = buildAttachmentDisplayGroups(
    attachmentsBySection,
    options,
  );

  const groups = displayGroups
    .filter(
      (group): group is NonNullable<(typeof displayGroups)[number]> =>
        group != null,
    )
    .map(group => {
      const safeGroupKey = resolveSafeGroupKey(group.groupKey);
      const stageLabel = resolveGroupedStageLabel(safeGroupKey, group.label);

      return {
        stage: safeGroupKey,
        stageLabel,
        substages: buildGroupedSubstages(
          safeGroupKey,
          stageLabel,
          group.items ?? [],
        ),
      };
    });

  debugAttachmentGrouping('buildGroupedAttachmentsFromSections.result', {
    display: summarizeDisplayGroups(displayGroups),
    grouped: summarizeGroupedAttachments(groups),
  });

  return groups;
}

/** Whether grouped view should split the facility-power family by nav scope. */
export function shouldSplitFacilityPowerAttachmentGroups(
  storedStepIds: string[] | null | undefined,
  enabledStepIds?: readonly string[],
): boolean {
  return isFacilityPowerFamilySplitScopeMode(storedStepIds, enabledStepIds);
}

export { STAGES };
